import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { lastValueFrom } from 'rxjs';
import { ICustomComponentTileDefinition } from '../../../dashboards/interfaces/tiles/definitions/custom-component-tile-definition.interface';
import { IDataSourceSettings } from '../../../data-source/interfaces/data-source-settings.interface';
import { DataSourceSettingsRef } from '../../../data-source/interfaces/data-source-settings.token';
import { TileCreatorService } from '../../services/tile-creator.service';
import { TILE_REQUEST } from '../../tokens/tile-request.token';

@Component({
  standalone: true,
  selector: 'user-custom-component-setup-wizard',
  imports: [
    AsyncPipe,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
  template: `
    <form [formGroup]="formGroup" (submit)="onSubmit()">
      <mat-form-field appearance="outline">
        <mat-label>Custom Component</mat-label>
        <mat-select formControlName="customComponentId" required>
          <mat-option value="new">Create New Component</mat-option>
          @for (customComponent of customComponents | async; track $index) {
            <mat-option [value]="customComponent._id">{{ customComponent.name }}</mat-option>
          }
        </mat-select>
        <mat-hint>Select one from the list.</mat-hint>
      </mat-form-field>

      @if (formGroup.value?.customComponentId === 'new') {
        <mat-form-field appearance="outline">
          <mat-label>Component Name</mat-label>
          <input matInput formControlName="name" maxlength="100" />
          <mat-hint>Enter a name for your new component.</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Component code</mat-label>
          <textarea matInput formControlName="moduleCode" cdkTextareaAutosize></textarea>
          <mat-hint>Enter your new component's ES module code.</mat-hint>
        </mat-form-field>
      }

      <button mat-flat-button color="primary" [disabled]="formGroup.invalid">Create Tile</button>
    </form>
  `,
  styles: `
    form {
      display: flex;
      flex-flow: column nowrap;
      gap: 24px;
    }
  `,
})
export class UserCustomComponentSetupWizardComponent {
  private readonly dataSourceSettings = inject(
    DataSourceSettingsRef,
  ) as unknown as IDataSourceSettings;

  private readonly httpClient = inject(HttpClient);

  private readonly tileCreatorService = inject(TileCreatorService);

  protected readonly tileDefinition = inject(TILE_REQUEST)
    .tileInstance as ICustomComponentTileDefinition;

  protected readonly formGroup = new FormGroup({
    customComponentId: new FormControl<string>(undefined),
    name: new FormControl<string>(undefined),
    moduleCode: new FormControl<string>(
      `import * as Informally from '@informally/sdk';

export default class extends Informally.CustomComponent {
    connectedCallback() {
        // initialization code
    }
}`,
    ),
  });

  protected readonly customComponents = this.httpClient.get<{ _id: string; name: string }[]>(
    this.dataSourceSettings.dataServiceApi + 'custom-components',
  );

  async onSubmit() {
    if (this.formGroup.valid) {
      const value = this.formGroup.value;

      if (value.customComponentId === 'new') {
        const customComponent = await lastValueFrom(
          this.httpClient.post<{ _id: string }>(
            this.dataSourceSettings.dataServiceApi + 'custom-component',
            {
              name: value.name,
              moduleCode: value.moduleCode,
            },
          ),
        );

        this.tileDefinition.customComponentId = customComponent._id;
      } else {
        this.tileDefinition.customComponentId = this.formGroup.value.customComponentId;
      }

      this.tileDefinition.tileType = 'custom-component';
      this.tileCreatorService.onChange.next();
    }
  }
}
